/**
 * Field Sync Rules — for each field of an Item linked to another system: which
 * system owns it, which way it moves, and what happens when both sides changed.
 *
 * Canon: docs/1-canon/01-terminology-and-glossary.md › "Field Source, Sync
 * Direction and Conflict Rule" (owner rulings 2026-10-15). This file is those
 * rulings as code, and the one copy of them: the browser (src/lib/syncEngine.ts,
 * src/lib/linkedItemSync.ts, the Settings pages), the `dispatch` function (values
 * going out) and the `apply` function (values coming in) all import it. Before
 * 2026-10-28 each had its own copy and they decided one event differently.
 *
 * Stored per connector in `connector_configs.trust_rules`, keyed by the Item
 * field key (`title`, `tags`, `attribute:effort`, …). A value is either the full
 * rule `{ owner, direction, conflict }` or one of the older single words
 * (`priorities_ai`, `external`, `negotiated`), which are read as the rule they
 * always meant. The key `owner` and the value `owner_wins` keep the spelling
 * they were saved with; in words people read they are the field's **Source**
 * and **Source wins** (canon renamed Field Owner to Field Source, 2026-10-28).
 *
 * Pure: no I/O, no Deno or browser globals.
 */

/** The field's Source: the system whose value is the Item's value for the field. */
export type FieldOwner = 'priorities_ai' | 'external';

/** none = not synced; in = into Priorities.ai; out = out of Priorities.ai; both = both ways. */
export type FieldSyncDirection = 'none' | 'in' | 'out' | 'both';

/** Only a field that syncs both ways has one. */
export type ConflictRule = 'owner_wins' | 'most_recent_wins' | 'person_decides';

export interface FieldSyncRule {
  owner: FieldOwner;
  direction: FieldSyncDirection;
  conflict: ConflictRule;
}

/** The single words trust_rules held before the full rule. */
export type LegacyFieldOwner = 'priorities_ai' | 'external' | 'negotiated';

export type StoredFieldRule = LegacyFieldOwner | Partial<FieldSyncRule>;
export type StoredFieldRules = Record<string, StoredFieldRule>;

/** A connector's own setting: which ways it may carry anything at all. */
export type ConnectorDirection = 'inbound' | 'outbound' | 'both';

export const NOT_SYNCED: FieldSyncRule = { owner: 'priorities_ai', direction: 'none', conflict: 'owner_wins' };

const LEGACY: Record<LegacyFieldOwner, FieldSyncRule> = {
  priorities_ai: { owner: 'priorities_ai', direction: 'out', conflict: 'owner_wins' },
  external: { owner: 'external', direction: 'in', conflict: 'owner_wins' },
  // "Negotiated" was a person deciding every incoming change.
  negotiated: { owner: 'priorities_ai', direction: 'both', conflict: 'person_decides' },
};

/**
 * What a field does when the connector has no rule saved for it. Planning
 * fields belong to Priorities.ai and go out; the work's own state belongs to the
 * tool the work is done in and comes in; the words that describe the work are
 * shared, and Priorities.ai wins when both sides changed (the canon default).
 */
export const DEFAULT_FIELD_RULES: Record<string, FieldSyncRule> = {
  priority_rank: LEGACY.priorities_ai,
  scope: LEGACY.priorities_ai,
  cycle_id: LEGACY.priorities_ai,
  status: LEGACY.external,
  assignee: LEGACY.external,
  time_spent: LEGACY.external,
  due_date: LEGACY.external,
  title: { owner: 'priorities_ai', direction: 'both', conflict: 'owner_wins' },
  description: { owner: 'priorities_ai', direction: 'both', conflict: 'owner_wins' },
  labels: { owner: 'priorities_ai', direction: 'both', conflict: 'owner_wins' },
  tags: { owner: 'priorities_ai', direction: 'both', conflict: 'owner_wins' },
};

const OWNERS: readonly FieldOwner[] = ['priorities_ai', 'external'];
const DIRECTIONS: readonly FieldSyncDirection[] = ['none', 'in', 'out', 'both'];
const CONFLICTS: readonly ConflictRule[] = ['owner_wins', 'most_recent_wins', 'person_decides'];

/** The source a direction implies when a rule does not name one. */
function ownerImpliedBy(direction: FieldSyncDirection): FieldOwner {
  return direction === 'in' ? 'external' : 'priorities_ai';
}

/** One stored value read as a whole rule; null when it is not a rule. */
export function readStoredRule(stored: unknown): FieldSyncRule | null {
  if (typeof stored === 'string') return (LEGACY as Record<string, FieldSyncRule>)[stored] ?? null;
  if (!stored || typeof stored !== 'object') return null;
  const s = stored as Partial<FieldSyncRule>;
  const direction = DIRECTIONS.includes(s.direction as FieldSyncDirection) ? (s.direction as FieldSyncDirection) : null;
  const owner = OWNERS.includes(s.owner as FieldOwner) ? (s.owner as FieldOwner) : null;
  if (!direction && !owner) return null;
  const d = direction ?? (owner === 'external' ? 'in' : 'out');
  return {
    owner: owner ?? ownerImpliedBy(d),
    direction: d,
    conflict: CONFLICTS.includes(s.conflict as ConflictRule) ? (s.conflict as ConflictRule) : 'owner_wins',
  };
}

/** A Field Mappings rule's direction (import / export / both) as a sync rule. */
export function ruleFromMappingDirection(direction: 'import' | 'export' | 'both' | string | undefined): FieldSyncRule | null {
  if (direction === 'import') return LEGACY.external;
  if (direction === 'export') return LEGACY.priorities_ai;
  if (direction === 'both') return { owner: 'priorities_ai', direction: 'both', conflict: 'owner_wins' };
  return null;
}

/**
 * The rule for one field of one connector: the field's own saved rule, else what
 * the connector's Field Mappings rule for that field says, else the connector's
 * '*' rule, else the default above, else not synced.
 */
export function fieldSyncRuleFor(
  rules: StoredFieldRules | null | undefined,
  field: string,
  fromMapping?: FieldSyncRule | null,
): FieldSyncRule {
  return (
    readStoredRule(rules?.[field]) ??
    fromMapping ??
    readStoredRule(rules?.['*']) ??
    DEFAULT_FIELD_RULES[field] ??
    NOT_SYNCED
  );
}

/** Why a rule cannot be saved (canon: a direction and a source that contradict each other), or null. */
export function fieldSyncRuleProblem(rule: FieldSyncRule): string | null {
  if (rule.direction === 'in' && rule.owner === 'priorities_ai') {
    return 'A field that only comes into Priorities.ai has the other system as its source.';
  }
  if ((rule.direction === 'out' || rule.direction === 'none') && rule.owner === 'external') {
    return rule.direction === 'out'
      ? 'A field that only goes out of Priorities.ai has Priorities.ai as its source.'
      : 'A field that does not sync has Priorities.ai as its source.';
  }
  if (rule.conflict !== 'owner_wins' && rule.direction !== 'both') {
    return 'Only a field that syncs both ways has a conflict rule.';
  }
  return null;
}

/** The rule as it is saved: one object per field. */
export function storedRuleOf(rule: FieldSyncRule): FieldSyncRule {
  return { owner: rule.owner, direction: rule.direction, conflict: rule.direction === 'both' ? rule.conflict : 'owner_wins' };
}

export function sendsOut(rule: FieldSyncRule, connector: ConnectorDirection = 'both'): boolean {
  return (rule.direction === 'out' || rule.direction === 'both') && connector !== 'inbound';
}

export function comesIn(rule: FieldSyncRule, connector: ConnectorDirection = 'both'): boolean {
  return (rule.direction === 'in' || rule.direction === 'both') && connector !== 'outbound';
}

/** Can a person change this field in Priorities.ai? Canon: a field kept elsewhere only when it syncs both ways. */
export function editableHere(rule: FieldSyncRule): boolean {
  return rule.owner === 'priorities_ai' || rule.direction === 'both';
}

// ── Coming in ────────────────────────────────────────────────────────────────

export interface IncomingChange {
  /** The other system's value differs from what it said at the last sync (or there was no last sync). */
  changedThere: boolean;
  /** The value here differs from what both sides agreed at the last sync. False when there was no last sync. */
  changedHere: boolean;
  /** The two values are already the same. */
  same: boolean;
  /** When the other system says this field changed. Only a per-field time counts; a whole-record stamp does not. */
  thereChangedAt?: string | null;
  /** When this field last changed here. */
  hereChangedAt?: string | null;
}

export type IncomingAction =
  /** Write the other system's value here. */
  | 'apply'
  /** Nothing to do: already the same, or nothing changed there. */
  | 'keep'
  /** The value here stands; the other value is kept and shown as a difference, and nobody is asked. */
  | 'difference'
  /** A person decides: the Conflict Queue. The value here stands until then. */
  | 'review';

export interface IncomingDecision {
  action: IncomingAction;
  /** The same field changed on both sides since it last synced. */
  conflict: boolean;
  /** Which side's value lost, when there was a conflict and a rule settled it: it goes to the Item's History. */
  loser?: 'here' | 'there';
  reason: string;
}

/**
 * What to do with the other system's value for one field. Canon: a change on the
 * non-source side of a one-way field is not a conflict; a conflict is the same
 * field changed on both sides since it last synced; the value that did not win
 * is kept.
 */
export function decideIncoming(
  rule: FieldSyncRule,
  change: IncomingChange,
  systemName: string,
  connector: ConnectorDirection = 'both',
): IncomingDecision {
  if (change.same) return { action: 'keep', conflict: false, reason: 'already the same' };
  if (!comesIn(rule, connector)) {
    return {
      action: 'difference',
      conflict: false,
      reason:
        rule.direction === 'none' || rule.direction === 'out'
          ? `this field does not come in from ${systemName}; its value there is kept as a difference`
          : `${systemName} is set to send nothing into Priorities.ai`,
    };
  }
  if (rule.direction === 'in') return { action: 'apply', conflict: false, reason: `${systemName} owns this field` };

  // Both ways.
  if (!change.changedThere) {
    return { action: 'keep', conflict: false, reason: `unchanged in ${systemName} since the last sync; the value here stands` };
  }
  if (!change.changedHere) return { action: 'apply', conflict: false, reason: `changed in ${systemName}` };

  const ownerSide: 'here' | 'there' = rule.owner === 'external' ? 'there' : 'here';
  const settle = (winner: 'here' | 'there', why: string): IncomingDecision => ({
    action: winner === 'there' ? 'apply' : 'keep',
    conflict: true,
    loser: winner === 'there' ? 'here' : 'there',
    reason: why,
  });

  if (rule.conflict === 'person_decides') {
    return { action: 'review', conflict: true, reason: 'changed on both sides; a person decides in the Conflict Queue' };
  }
  if (rule.conflict === 'most_recent_wins') {
    const there = change.thereChangedAt ? Date.parse(change.thereChangedAt) : NaN;
    const here = change.hereChangedAt ? Date.parse(change.hereChangedAt) : NaN;
    if (Number.isFinite(there) && Number.isFinite(here)) {
      return there > here
        ? settle('there', `changed on both sides; the change in ${systemName} is the more recent`)
        : settle('here', 'changed on both sides; the change here is the more recent');
    }
    // Canon: without the other system's per-field time, Most recent wins falls back to Source wins.
    return settle(
      ownerSide,
      `changed on both sides; ${systemName} does not say when this field changed, so the source's value stands`,
    );
  }
  return settle(ownerSide, `changed on both sides; the source (${ownerSide === 'here' ? 'Priorities.ai' : systemName}) wins`);
}

// ── Words ───────────────────────────────────────────────────────────────────

export const DIRECTION_WORDS: Record<FieldSyncDirection, string> = {
  none: 'Not synced',
  in: 'Into Priorities.ai',
  out: 'Out of Priorities.ai',
  both: 'Both ways',
};

export const CONFLICT_WORDS: Record<ConflictRule, string> = {
  owner_wins: 'Source wins',
  most_recent_wins: 'Most recent wins',
  person_decides: 'A person decides',
};

export function ownerWords(owner: FieldOwner, systemName: string): string {
  return owner === 'priorities_ai' ? 'Priorities.ai' : systemName;
}

// ── The value that did not win ───────────────────────────────────────────────

/** History's event for a value a sync set aside (canon: the value that did not win is kept). */
export const SET_ASIDE_EVENT = 'SYNC_VALUE_SET_ASIDE';

/**
 * The audit_events row that keeps a value a sync did not use, so the Item's
 * History shows it with the system it came from and it can be put back.
 */
export function setAsideAuditRow(input: {
  workspaceId: string;
  itemId: string;
  /** A strategy object instead of an Item (./syncedObjects.ts): its History reads the row by these. */
  entityType?: string;
  /** The Item field key: title, description, tags, attribute:effort … */
  field: string;
  /** The value that was set aside. */
  value: unknown;
  /** Where that value came from: "Priorities.ai" or the linked system's name. */
  from: string;
  /** The linked system the sync was with. */
  systemName: string;
  reason: string;
  /** For an attribute: the scale the value is on. */
  scaleId?: string | null;
}): Record<string, unknown> {
  const isItem = !input.entityType || input.entityType === 'item';
  return {
    workspace_id: input.workspaceId,
    item_id: isItem ? input.itemId : null,
    event_type: SET_ASIDE_EVENT,
    created_by: null,
    payload: {
      ...(isItem ? {} : { entityType: input.entityType, entityId: input.itemId }),
      field: input.field,
      value: input.value ?? null,
      from: input.from,
      systemName: input.systemName,
      reason: input.reason,
      ...(input.scaleId ? { scaleId: input.scaleId } : {}),
    },
  };
}
